<template>
  <div class="card card-body shadow-sm mt-0">
    <div class="form-header d-flex justify-content-between align-items-center">
      <h4 class="form-title">{{ pessoa.id ? 'Editar Pessoa Jurídica' : 'Nova Pessoa Jurídica' }}</h4>
      <button class="btn btn-secondary btn-sm me-3" @click="voltar">Voltar</button>
    </div>
    <BaseForm :isFormValid="isFormValid" :isSubmitting="isSubmitting" @submit="salvarPessoa" @cancel="voltar">

      <!-- Dados da Empresa -->
      <FormSection title="Dados da Empresa">
        <InputField 
          v-model="pessoa.razao_social" 
          label="Razão Social <span class='required'>*</span>" 
          id="razao_social" 
          :class="{ 'error-input': formTouched && errors.razao_social }"
          width="300px"
          required
        />

        <InputField 
          v-model="pessoa.nome_fantasia" 
          label="Nome Fantasia <span class='required'>*</span>" 
          id="nome_fantasia" 
          :class="{ 'error-input': formTouched && errors.nome_fantasia }"
          width="300px"
          required
        />

        <InputField 
          v-model="pessoa.cnpj" 
          label="CNPJ <span class='required'>*</span>" 
          id="cnpj" 
          mask="cnpj" 
          :class="{ 'error-input': errors.cnpj }"
          required
        />
        <span v-show="errors.cnpj" class="error-message">{{ errors.cnpj }}</span>

        <InputField 
          v-model="pessoa.telefone1" 
          label="Telefone" 
          id="telefone1" 
          mask="telefone"
          maxlength="14"
          :class="{ 'error-input': showTelefone1Error }"
          @blur="onTelefone1Blur"
        />
        <!-- <span v-show="showTelefone1Error" class="error-message">{{ errors.telefone1 }}</span> -->

        <InputField 
          v-model="pessoa.telefone2" 
          label="Celular <span class='required'>*</span>" 
          id="telefone2" 
          mask="celular"
          maxlength="15"
          :class="{ 'error-input': showTelefone2Error }"
          @blur="onTelefone2Blur"
        />
        <!-- <span v-show="showTelefone2Error" class="error-message">{{ errors.telefone2 }}</span> -->

        <InputField 
          v-model="pessoa.email" 
          label="Email <span class='required'>*</span>" 
          id="email" 
          :class="{ 'error-input': formTouched && errors.email }"
          width="300px"
        />
      </FormSection>

      <!-- Endereço -->
      <FormSection title="Endereço">
        <InputField 
          v-model="pessoa.cep" 
          label="CEP <span class='required'>*</span>" 
          id="cep" 
          mask="cep" 
          :class="{ 'error-input': formTouched && errors.cep }"
          width="80px"
        />

        <InputField 
          v-model="pessoa.endereco" 
          label="Endereço <span class='required'>*</span>" 
          id="endereco" 
          :class="{ 'error-input': formTouched && errors.endereco }"
          width="300px"
        />

        <InputField 
          v-model="pessoa.numero" 
          label="Número <span class='required'>*</span>" 
          id="numero"
          :class="{ 'error-input': formTouched && errors.numero }"
          width="70px"
        />

        <InputField 
          v-model="pessoa.bairro" 
          label="Bairro <span class='required'>*</span>" 
          id="bairro"
          :class="{ 'error-input': formTouched && errors.bairro }"
          width="250px"
        />

        <InputField 
          v-model="pessoa.complemento" 
          label="Complemento" 
          id="complemento"
        />
        
        <SelectField 
          v-if="estados.length > 0" 
          v-model="pessoa.estado_cod" 
          label="Estado <span class='required'>*</span>" 
          id="estado" 
          :options="estados.map(e => ({ value: e.id, text: `${e.nome} (${e.sigla})` }))"
          @change="carregarMunicipios"
          :class="{ 'error-input': formTouched && errors.estado_cod }"
        />

        <SelectField 
          v-if="municipios.length > 0" 
          v-model="pessoa.municipio_cod" 
          label="Município <span class='required'>*</span>" 
          id="municipio" 
          :options="municipios.map(m => ({ value: m.id, text: m.nome }))"
          :class="{ 'error-input': formTouched && errors.municipio_cod }"
        />
      </FormSection>

      <!-- Observações -->
      <FormSection title="Observações">
        <textarea 
          v-model="pessoa.observacoes" 
          id="observacoes" 
          rows="3" 
          class="form-control"
          placeholder="Adicione observações"
        ></textarea>
      </FormSection>
    </BaseForm>
  </div>
</template>

<script>
import { reactive, ref, computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import axios from '@/services/axios';
import InputField from '@/components/Layout/InputField.vue';
import SelectField from '@/components/Layout/SelectField.vue';
import FormSection from '@/components/Layout/FormSection.vue';
import BaseForm from '@/components/Layout/BaseForm.vue';
import globalUtils from '@/utils/globalUtils.js';

export default {
  components: { InputField, SelectField, FormSection, BaseForm },
  setup() {
    const route = useRoute();
    const router = useRouter();

    const pessoa = reactive({
      id: null,
      razao_social: '',
      nome_fantasia: '',
      cnpj: '',
      telefone1: '',
      telefone2: '',
      email: '',
      cep: '',
      endereco: '',
      numero: '',
      bairro: '',
      complemento: '',
      estado_cod: '',
      municipio_cod: '',
      observacoes: '',
    });

    const estados = ref([]);
    const municipios = ref([]);
    const errors = reactive({});
    const formTouched = ref(false);
    const isSubmitting = ref(false);

    const touched = reactive({
      telefone1: false,
      telefone2: false,
    });    

    // Flag para indicar que o carregamento inicial dos dados já foi concluído
    const initialLoadComplete = ref(false);

    const limparTelefone = (valor) => (valor || '').replace(/\D/g, '');

    const validarTelefoneFixo = (valor) => {
      const digits = limparTelefone(valor);
      if (!digits) return '';
      if (digits.length !== 10) return 'Telefone deve ter 10 dígitos.';
      return '';
    };

    const validarCelular = (valor) => {
      const digits = limparTelefone(valor);
      if (!digits) return 'Celular é obrigatório.';
      if (digits.length !== 11) return 'Celular deve ter 11 dígitos.';
      return '';
    };

    const onTelefone1Blur = () => {
      touched.telefone1 = true;
      errors.telefone1 = validarTelefoneFixo(pessoa.telefone1);
    };

    const onTelefone2Blur = () => {
      touched.telefone2 = true;
      errors.telefone2 = validarCelular(pessoa.telefone2);
    };    

    watch(() => pessoa.telefone1, () => {
      if (touched.telefone1 || formTouched.value) {
        errors.telefone1 = validarTelefoneFixo(pessoa.telefone1);
      } else {
        errors.telefone1 = '';
      }
    });

    watch(() => pessoa.telefone2, () => {
      if (touched.telefone2 || formTouched.value) {
        errors.telefone2 = validarCelular(pessoa.telefone2);
      } else {
        errors.telefone2 = '';
      }
    });    

    // Watcher para validar CNPJ
    watch(() => pessoa.cnpj, (newCnpj) => {
      const cnpjNumeros = newCnpj.replace(/\D/g, '');
      if (cnpjNumeros.length < 14) {
        errors.cnpj = '';
      } else if (cnpjNumeros.length === 14) {
        errors.cnpj = !globalUtils.validarCNPJ(newCnpj)
          ? 'CNPJ inválido.'
          : '';
      }
    });

    // Watcher para CEP (atualiza os campos de endereço, estado e município)
    watch(() => pessoa.cep, async (newCep) => {
      // Evita executar a lógica se os dados iniciais ainda não foram carregados
      if (!initialLoadComplete.value) return;

      const cepLimpo = newCep ? newCep.replace(/\D/g, '') : '';
      if (cepLimpo.length === 8) {
        try {
          const enderecoData = await globalUtils.buscarEnderecoPorCEP(newCep);
          pessoa.endereco = enderecoData.endereco || '';
          pessoa.bairro = enderecoData.bairro || '';
          pessoa.complemento = enderecoData.complemento || '';

          // Atualiza estado e município:
          const estadoEncontrado = estados.value.find(e =>
            e.sigla.toUpperCase() === enderecoData.estado.toUpperCase()
          );
          if (estadoEncontrado) {
            pessoa.estado_cod = estadoEncontrado.id;
            await carregarMunicipios();

            // Função auxiliar para normalizar strings
            const normalizeString = (str) =>
              str ? str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim() : '';

            const municipioEncontrado = municipios.value.find(m =>
              normalizeString(m.nome) === normalizeString(enderecoData.municipio)
            );
            pessoa.municipio_cod = municipioEncontrado ? municipioEncontrado.id : '';
          }
        } catch (error) {
          console.error("Erro ao buscar endereço pelo CEP:", error.message);
        }
      }
    });

    const isFormValid = computed(() => {
      return (
        pessoa.razao_social.trim() !== '' &&
        pessoa.nome_fantasia.trim() !== '' &&
        pessoa.cnpj.trim() !== '' &&
        globalUtils.validarCNPJ(pessoa.cnpj) &&
        pessoa.telefone2.trim() !== '' &&
        !validarTelefoneFixo(pessoa.telefone1) &&
        !validarCelular(pessoa.telefone2) &&
        pessoa.email.trim() !== '' &&
        pessoa.cep.trim() !== '' &&
        pessoa.endereco.trim() !== '' &&
        (pessoa.numero !== null && pessoa.numero.toString().trim() !== '') &&
        pessoa.bairro.trim() !== '' &&
        (pessoa.municipio_cod !== null && pessoa.municipio_cod.toString().trim() !== '') &&
        (pessoa.estado_cod !== null && pessoa.estado_cod.toString().trim() !== '') &&
        !Object.values(errors).some((error) => error)
      );
    });

    const showTelefone1Error = computed(() => !!errors.telefone1 && (touched.telefone1 || formTouched.value));
    const showTelefone2Error = computed(() => !!errors.telefone2 && (touched.telefone2 || formTouched.value));    

    const carregarEstados = async () => {
      try {
        const response = await axios.get(`/localidades/estados`);
        estados.value = response.data;
      } catch (error) {
        console.error('Erro ao carregar estados:', error);
      }
    };

    const carregarMunicipios = async () => {
      try {
        if (!pessoa.estado_cod) {
          municipios.value = [];
          return;
        }
        const response = await axios.get(`/localidades/municipios/${pessoa.estado_cod}`);
        municipios.value = response.data;
      } catch (error) {
        console.error('Erro ao carregar municípios:', error);
      }
    };

    const salvarPessoa = async () => {
      if (isSubmitting.value) return;

      formTouched.value = true;
      touched.telefone1 = true;
      touched.telefone2 = true;

      errors.telefone1 = validarTelefoneFixo(pessoa.telefone1);
      errors.telefone2 = validarCelular(pessoa.telefone2);

      if (!isFormValid.value) {
        const mensagens = Object.values(errors).filter(Boolean);

        alert(
          mensagens.length
            ? `Não foi possível salvar:\n- ${mensagens.join('\n- ')}`
            : 'Não foi possível salvar. Verifique os campos obrigatórios.'
        );

        isSubmitting.value = false;
        return;
      }

      try {
        isSubmitting.value = true;

        const url = pessoa.id
          ? `/cadastros/pessoas-juridicas/${pessoa.id}`
          : `/cadastros/pessoas-juridicas`;
        const method = pessoa.id ? 'put' : 'post';

        await axios[method](url, pessoa);
        router.push('/cadastros/pessoas-juridicas');
      } catch (error) {
        console.error('Erro ao salvar pessoa jurídica:', error.response?.data || error.message);
        alert(`Erro ao salvar pessoa jurídica: ${
          error.response?.data?.error ||
          error.response?.data?.message ||
          error.message ||
          'Erro desconhecido'
        }`);
      } finally {
        isSubmitting.value = false;
      }
    };

    const voltar = () => {
      router.push('/cadastros/pessoas-juridicas');
    };

    onMounted(async () => {
      await carregarEstados();
      const pessoaId = route.params.id;
      if (pessoaId) {
        try {
          const response = await axios.get(`/cadastros/pessoas-juridicas/${pessoaId}`);
          Object.assign(pessoa, response.data);
          if (pessoa.estado_cod) {
            await carregarMunicipios();
          }
        } catch (error) {
          console.error('Erro ao carregar pessoa jurídica:', error);
        }
      }
      // Sinaliza que os dados iniciais já foram carregados
      initialLoadComplete.value = true;
    });

    return {
      pessoa,
      estados,
      municipios,
      errors,
      formTouched,
      isFormValid,
      isSubmitting,
      salvarPessoa,
      voltar,
      carregarEstados,
      carregarMunicipios,

      touched,
      showTelefone1Error,
      showTelefone2Error,
      onTelefone1Blur,
      onTelefone2Blur,      
    };
  },
};
</script>

